import { VesselMessage } from '../../../../types/VesselMessage';
import { VesselUpdateRequestMessage } from '../../../../types/VesselUpdateRequestMessage';
import { useDrawer } from '../../../cdl/Drawer/useDrawer.hook';
import { IconButton } from '../../../cdl/IconButton/IconButton';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { IconPencil } from '../../../common/icons/cdl/Pencil';
import { Section } from '../../../common/Section/Section';
import { EditVesselForm } from '../EditVessel/EditVesselForm';
import type { LubricationChartUpload } from '../hooks/useLubricationChartUpload';
import { FileMode, useVesselUpdate } from '../hooks/useVesselUpdate';

import { VesselDetails } from './VesselDetails';

interface InfoSectionProps {
    vessel: VesselMessage;
    lubricationChartUpload: LubricationChartUpload;
}

export const VesselDetailsSection = ({ vessel, lubricationChartUpload }: InfoSectionProps) => {
    const { push, pop } = useDrawer();
    const role = useRole();

    const { mutateAsync: mutateVessel } = useVesselUpdate(vessel.id, lubricationChartUpload.uploadLubricationChart);

    const onEditClick = () => {
        const parseEditClose = async (editedVessel: VesselUpdateRequestMessage) => {
            const mutationOptions = { onSuccess: () => pop(drawerId) };

            if (editedVessel.lubricationChart?.added) {
                await mutateVessel(
                    {
                        vesselId: vessel.id,
                        vessel: editedVessel,
                        lubricationChart: editedVessel.lubricationChart.file,
                        fileMode: FileMode.ADDED,
                    },
                    mutationOptions
                );
            } else if (editedVessel.lubricationChart?.removed) {
                await mutateVessel(
                    {
                        vesselId: vessel.id,
                        vessel: editedVessel,
                        fileMode: FileMode.REMOVED,
                    },
                    mutationOptions
                );
            } else {
                await mutateVessel(
                    {
                        vesselId: vessel.id,
                        vessel: editedVessel,
                    },
                    mutationOptions
                );
            }
        };

        const drawerId = push({
            header: { title: translate('vessel.edit.editVessel') },
            content: <EditVesselForm onSubmit={parseEditClose} vessel={vessel} />,
        });
    };

    return (
        <Section
            title={translate('vessel.info')}
            actions={
                role.hasWriteRights(vessel.customerId) ? (
                    <IconButton
                        emphasis="medium"
                        Icon={IconPencil}
                        onClick={onEditClick}
                        tooltipLabel={translate('vessel.edit.editVessel')}
                    />
                ) : null
            }
        >
            <VesselDetails vessel={vessel} />
        </Section>
    );
};
